// Rysowanie klockow. Wspolny wyglad dla podgladu w edytorze i dla symulacji -
// dzieki temu "to, co zbudowales, wyglada tak samo w probie".
//
// Detale sa PROCEDURALNE (bez zadnych assetow): rysujemy je z tych samych
// danych, co kolizje, wiec grafika nie moze rozjechac sie z fizyka.
// Wszystko jest ostre przy kazdym zoomie i na kazdym ekranie.

import type { PartDefinition, PartPattern, Vec2Like } from '../domain/types';
import { METERS_PER_GRID } from '../physics/units';
import type { Camera } from './camera';

/** Umiejscowienie czesci w swiecie. */
export interface PartPlacement {
  definition: PartDefinition;
  /** Origin czesci w swiecie (metry). */
  originX: number;
  originY: number;
  /** Kat obrotu czesci w swiecie (radiany). */
  angle: number;
  /**
   * Srodek obrotu w lokalnych jednostkach kratki, liczony od origina.
   * Domylnie { x: 0, y: 0 } - tak obraca sie klocek w korpusie robota.
   * Czesc osadzona na wale musi podac tu polozenie swojego socketu 'axle',
   * bo origin to naroze klatki, a nie srodek tarczy - bez tego klocek
   * przy obrocie "odjezdza" od osi walu.
   */
  pivot?: Vec2Like;
}

export interface PartStyle {
  fill?: string;
  stroke?: string;
  accent?: string;
  alpha?: number;
  /** Grubosc obrysu w pikselach ekranu. */
  lineWidth?: number;
}

function clamp255(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((char) => char + char)
          .join('')
      : clean;
  const value = Number.parseInt(full.slice(0, 6), 16);
  if (!Number.isFinite(value)) return { r: 128, g: 128, b: 128 };
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

function toHex(color: { r: number; g: number; b: number }): string {
  return `#${[color.r, color.g, color.b]
    .map((channel) => clamp255(channel).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Rozjasnia kolor w strone bieli - do detali i podswietlen. */
export function lighten(hex: string, amount: number): string {
  const { r, g, b } = parseHex(hex);
  return toHex({
    r: r + (255 - r) * amount,
    g: g + (255 - g) * amount,
    b: b + (255 - b) * amount,
  });
}

/** Przyciemnia kolor - do cieni i gradientow. */
export function darken(hex: string, amount: number): string {
  const { r, g, b } = parseHex(hex);
  return toHex({ r: r * (1 - amount), g: g * (1 - amount), b: b * (1 - amount) });
}

/** Wzor domyslny dla czesci bez wlasnego wpisu w katalogu. */
export function defaultPattern(definition: PartDefinition): PartPattern {
  switch (definition.category) {
    case 'wheel':
      return 'tread';
    case 'battery':
      return 'cell';
    case 'drive':
      return 'motor';
    case 'gear':
      return 'gear';
    // Rama, mocowania i ciezary to czyste bryle - czytelniejsze BEZ paskow,
    // a kategoria odroznia sie kolorem (plus nity w miejscach mocowan).
    default:
      return 'plain';
  }
}

/** Zeby zebatki wokol okregu - zeby od razu bylo widac, ze to zebatka, a nie kolo. */
function drawGearTeeth(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  accent: string,
): void {
  const teeth = Math.max(8, Math.round(r * 24));
  ctx.strokeStyle = accent;
  ctx.lineWidth = 0.06;
  ctx.beginPath();
  for (let index = 0; index < teeth; index += 1) {
    const angle = (index / teeth) * Math.PI * 2;
    ctx.moveTo(cx + Math.cos(angle) * r * 0.82, cy + Math.sin(angle) * r * 0.82);
    ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
  }
  ctx.stroke();

  // Piasta z krzyzakiem - srodek zebatki.
  ctx.lineWidth = 0.05;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.24, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  for (let index = 0; index < 3; index += 1) {
    const angle = (index / 3) * Math.PI * 2;
    ctx.moveTo(cx - Math.cos(angle) * r * 0.24, cy - Math.sin(angle) * r * 0.24);
    ctx.lineTo(cx + Math.cos(angle) * r * 0.24, cy + Math.sin(angle) * r * 0.24);
  }
  ctx.stroke();
}

/** Detale rysowane wewnatrz klocka (wspolrzedne kratki, os Y w gore). */
function drawPattern(
  ctx: CanvasRenderingContext2D,
  pattern: PartPattern,
  accent: string,
  box: { x: number; y: number; w: number; h: number },
): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.w, box.h);
  ctx.clip();
  ctx.strokeStyle = accent;
  ctx.fillStyle = accent;
  ctx.lineWidth = 0.05;

  switch (pattern) {
    case 'plain':
      break;
    case 'motor': {
      // Wirnik silnika: tarcza z zebrami - od razu widac, ze to modul napedu.
      const cx = box.x + box.w / 2;
      const cy = box.y + box.h / 2;
      const r = Math.min(box.w, box.h) * 0.24;
      ctx.lineWidth = 0.05;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
      for (let index = 0; index < 6; index += 1) {
        const angle = (index / 6) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
        ctx.stroke();
      }
      break;
    }
    case 'ribbed': {
      const step = 0.33;
      ctx.beginPath();
      for (let x = box.x + step; x < box.x + box.w - 0.02; x += step) {
        ctx.moveTo(x, box.y + 0.18);
        ctx.lineTo(x, box.y + box.h - 0.18);
      }
      ctx.stroke();
      break;
    }
    case 'plate': {
      const step = 0.5;
      for (let x = box.x + step / 2; x < box.x + box.w; x += step) {
        for (let y = box.y + step / 2; y < box.y + box.h; y += step) {
          ctx.beginPath();
          ctx.arc(x, y, 0.07, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      break;
    }
    case 'cell': {
      const step = 0.25;
      ctx.beginPath();
      for (let y = box.y + step; y < box.y + box.h; y += step) {
        ctx.moveTo(box.x + 0.14, y);
        ctx.lineTo(box.x + box.w - 0.14, y);
      }
      ctx.stroke();
      break;
    }
    case 'fins': {
      const step = 0.28;
      ctx.beginPath();
      for (let x = box.x - box.h; x < box.x + box.w; x += step) {
        ctx.moveTo(x, box.y);
        ctx.lineTo(x + box.h, box.y + box.h);
      }
      ctx.stroke();
      break;
    }
    case 'ballast': {
      const step = 0.2;
      ctx.lineWidth = 0.07;
      ctx.beginPath();
      for (let x = box.x - box.h; x < box.x + box.w; x += step) {
        ctx.moveTo(x, box.y);
        ctx.lineTo(x + box.h, box.y + box.h);
        ctx.moveTo(x + box.h, box.y);
        ctx.lineTo(x, box.y + box.h);
      }
      ctx.stroke();
      break;
    }
    case 'gear': {
      const teeth = 8;
      for (let index = 0; index < teeth; index += 1) {
        const x = box.x + (box.w * (index + 0.5)) / teeth;
        ctx.fillRect(x - 0.06, box.y + box.h - 0.2, 0.12, 0.16);
      }
      break;
    }
    case 'tread':
    default:
      break;
  }

  ctx.restore();
}

/** Nity w miejscach mocowan - pomagaja zrozumiec, gdzie mozna dokladac klocki. */
function drawSocketBolts(
  ctx: CanvasRenderingContext2D,
  definition: PartDefinition,
  accent: string,
): void {
  for (const socket of definition.sockets) {
    if (socket.kind === 'axle') {
      // Os: pierscien zamiast nitu - od razu widac, gdzie mozna osadzic wal.
      ctx.strokeStyle = accent;
      ctx.lineWidth = 0.055;
      ctx.beginPath();
      ctx.arc(socket.x, socket.y, 0.14, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(socket.x, socket.y, 0.045, 0, Math.PI * 2);
      ctx.fillStyle = accent;
      ctx.fill();
    } else {
      ctx.fillStyle = accent;
      ctx.beginPath();
      ctx.arc(socket.x, socket.y, 0.08, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * Rysuje jeden klocek: bryle, detale i nity w miejscach mocowan.
 * Rysujemy w ukladzie kratki (os Y w gore), wiec uzywana geometria jest
 * dokladnie ta sama, ktora trafia do fizyki.
 */
export function drawPart(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  placement: PartPlacement,
  style: PartStyle = {},
): void {
  const definition = placement.definition;
  const pixelsPerGrid = camera.scale * METERS_PER_GRID;
  if (pixelsPerGrid <= 0) return;

  const pivot = placement.pivot ?? { x: 0, y: 0 };
  const anchor = camera.worldToScreen(
    placement.originX + pivot.x * METERS_PER_GRID,
    placement.originY + pivot.y * METERS_PER_GRID,
  );
  const fill = style.fill ?? definition.appearance.fill;
  const stroke = style.stroke ?? definition.appearance.stroke;
  const accent = style.accent ?? definition.appearance.accent ?? lighten(fill, 0.28);
  const pattern = definition.appearance.pattern ?? defaultPattern(definition);
  const lineWidth = (style.lineWidth ?? 1.5) / pixelsPerGrid;

  ctx.save();
  ctx.globalAlpha = style.alpha ?? 1;
  ctx.translate(anchor.x, anchor.y);
  ctx.rotate(-placement.angle); // ekran ma os Y w dol
  ctx.scale(pixelsPerGrid, -pixelsPerGrid); // odtad rysujemy w jednostkach kratki
  ctx.translate(-pivot.x, -pivot.y); // pivot wskazuje teraz na srodek obrotu

  for (const shape of definition.shapes) {
    if (shape.kind === 'box') {
      const x = shape.x - shape.w / 2;
      const y = shape.y - shape.h / 2;
      const gradient = ctx.createLinearGradient(0, shape.y + shape.h / 2, 0, shape.y - shape.h / 2);
      gradient.addColorStop(0, lighten(fill, 0.24));
      gradient.addColorStop(1, darken(fill, 0.16));

      ctx.beginPath();
      ctx.roundRect(x, y, shape.w, shape.h, 0.12);
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = stroke;
      ctx.stroke();

      drawPattern(ctx, pattern, accent, { x, y, w: shape.w, h: shape.h });
    } else {
      const gradient = ctx.createLinearGradient(0, shape.y + shape.r, 0, shape.y - shape.r);
      gradient.addColorStop(0, lighten(fill, 0.22));
      gradient.addColorStop(1, darken(fill, 0.2));

      ctx.beginPath();
      ctx.arc(shape.x, shape.y, shape.r, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = stroke;
      ctx.stroke();

      if (pattern === 'gear') {
        drawGearTeeth(ctx, shape.x, shape.y, shape.r, accent);
      }
    }
  }

  drawSocketBolts(ctx, definition, accent);
  ctx.restore();
}

export interface WheelPlacement {
  definition: PartDefinition;
  /** Srodek kola w swiecie (metry). */
  centerX: number;
  centerY: number;
  /** Kat obrotu kola (radiany) - bieznik i szprychy obracaja sie razem z kolem. */
  angle: number;
  radiusMeters: number;
}

/** Kolo: opona z biezkiem, piasta i szprychami (widac obrot i poslizg). */
export function drawWheel(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  wheel: WheelPlacement,
  style: PartStyle = {},
): void {
  const radius = wheel.radiusMeters * camera.scale;
  if (radius <= 1) return;

  const center = camera.worldToScreen(wheel.centerX, wheel.centerY);
  const fill = style.fill ?? wheel.definition.appearance.fill;
  const stroke = style.stroke ?? wheel.definition.appearance.stroke;
  const accent = style.accent ?? wheel.definition.appearance.accent ?? lighten(fill, 0.25);

  ctx.save();
  ctx.globalAlpha = style.alpha ?? 1;

  const gradient = ctx.createRadialGradient(
    center.x - radius * 0.3,
    center.y - radius * 0.3,
    radius * 0.1,
    center.x,
    center.y,
    radius,
  );
  gradient.addColorStop(0, lighten(fill, 0.3));
  gradient.addColorStop(1, darken(fill, 0.25));

  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.lineWidth = style.lineWidth ?? 2;
  ctx.strokeStyle = stroke;
  ctx.stroke();

  // Bieznik i szprychy w ukladzie kola, wiec obracaja sie razem z nim.
  ctx.save();
  ctx.translate(center.x, center.y);
  ctx.rotate(-wheel.angle);

  const treads = Math.max(8, Math.round(radius / 3));
  ctx.strokeStyle = accent;
  ctx.lineWidth = Math.max(1, radius * 0.06);
  ctx.beginPath();
  for (let index = 0; index < treads; index += 1) {
    const angle = (index / treads) * Math.PI * 2;
    ctx.moveTo(Math.cos(angle) * radius * 0.78, Math.sin(angle) * radius * 0.78);
    ctx.lineTo(Math.cos(angle) * radius * 0.96, Math.sin(angle) * radius * 0.96);
  }
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(0, 0, radius * 0.34, 0, Math.PI * 2);
  ctx.fillStyle = darken(fill, 0.35);
  ctx.fill();
  ctx.strokeStyle = accent;
  ctx.lineWidth = Math.max(1, radius * 0.05);
  ctx.stroke();

  ctx.beginPath();
  for (let index = 0; index < 3; index += 1) {
    const angle = (index / 3) * Math.PI * 2;
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(angle) * radius * 0.34, Math.sin(angle) * radius * 0.34);
  }
  ctx.stroke();
  ctx.restore();

  ctx.restore();
}

export type SocketHintState = 'free' | 'compatible';

/**
 * Znacznik mocowania: pierscien plus krotka kreska pokazujaca kierunek montazu.
 * To najwazniejsza podpowiedz w edytorze - uczy, jak dziala system zlacz.
 */
export function drawSocketHint(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  grid: Vec2Like,
  normal: Vec2Like | null,
  state: SocketHintState,
): void {
  const point = camera.worldToScreen(grid.x * METERS_PER_GRID, grid.y * METERS_PER_GRID);
  const ring = state === 'compatible' ? '#6bd18a' : '#3c4a63';
  const fill = state === 'compatible' ? '#1e5a33' : '#232c3b';
  const radius = state === 'compatible' ? 6 : 4;

  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = ring;
  ctx.stroke();

  if (normal) {
    const length = state === 'compatible' ? 11 : 7;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    // Ekran ma os Y w dol, wiec pionowa skladowa normalnej zmienia znak.
    ctx.lineTo(point.x + normal.x * length, point.y - normal.y * length);
    ctx.strokeStyle = ring;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}